import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import {
  formatDate,
  formatViews,
  getCategoryBySlug,
  getCategoryName,
  getMovieById,
  getRelatedMovies,
} from "@/utils/catalog";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const related = movie ? getRelatedMovies(movie, 4) : [];
  const [toast, setToast] = useState("");
  const [watchOpen, setWatchOpen] = useState(false);

  usePageMeta(
    movie ? `${movie.title} - 黄色片免费看` : "影片未找到 - 黄色片免费看",
    movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.summary}`
      : "没有找到该影片，可能链接已经失效。",
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("影片链接已复制到剪贴板");
    } catch {
      setToast(`复制失败，可手动复制地址：${url}`);
    }
  };

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100/60 p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-file-search-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              影片未找到
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              这部影片可能已经下架，或者链接地址有误，去首页看看其它资源吧。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
            >
              <i className="ri-home-4-line" /> 返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategoryBySlug(movie.category);
  const metaList = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast },
    { label: "类型", value: movie.tags.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "上映年份", value: movie.year },
    { label: "时长", value: movie.duration },
    { label: "更新时间", value: formatDate(movie.updatedAt) },
    { label: "浏览热度", value: `${formatViews(movie.views)} 次` },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50 pb-16 lg:pb-0">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${movie.category}`} className="hover:text-primary-600">
              {getCategoryName(movie.category)}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{movie.title}</span>
          </nav>

          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-8">
            <div>
              <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 影片封面`}
                  title={`${movie.title} - ${getCategoryName(movie.category)}`}
                  className="h-[420px] w-full object-cover object-top md:h-[440px]"
                />
                <span className="absolute left-3 top-3 rounded-md bg-foreground-950/75 px-2 py-1 text-xs font-medium text-accent-300">
                  {movie.rating.toFixed(1)} 分
                </span>
              </div>

              <div className="mt-3 hidden gap-2 lg:flex">
                <button
                  type="button"
                  onClick={() => setWatchOpen(true)}
                  className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-base" /> 立即观看
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line" /> 分享
                </button>
              </div>

              <div className="mt-3 rounded-lg border border-background-200 bg-background-100/60 p-3.5">
                <p className="text-xs text-foreground-500">所属分类</p>
                <Link
                  to={`/category/${movie.category}`}
                  className="mt-2 flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:text-primary-600"
                >
                  <span className="flex items-center gap-2">
                    <i className={`${category?.icon ?? "ri-film-line"} text-base text-primary-500`} />
                    {getCategoryName(movie.category)}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400" />
                </Link>
              </div>
            </div>

            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-[32px]">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-900">
                  {movie.year}
                </span>
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                  {movie.region}
                </span>
                <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                  {movie.duration}
                </span>
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <p className="mt-4 rounded-lg border-l-2 border-primary-500 bg-background-100/70 px-4 py-3 text-sm leading-relaxed text-foreground-700">
                {movie.summary}
              </p>

              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2 md:p-5">
                {metaList.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 text-sm">
                    <span className="w-16 shrink-0 text-foreground-500">{item.label}</span>
                    <span className="min-w-0 flex-1 text-foreground-800">{item.value}</span>
                  </div>
                ))}
              </div>

              <section className="mt-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950 md:text-lg">
                  <span className="h-4 w-0.5 rounded-full bg-primary-500" />
                  影片简介
                </h2>
                <div className="mt-3 space-y-3 text-sm leading-7 text-foreground-700">
                  <p>{movie.description}</p>
                  <p className="text-foreground-500">
                    本站影片资料为演示示例内容，页面仅展示影片基础信息，不提供真实播放、下载或收藏功能。
                  </p>
                </div>
              </section>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <Link
                  to={`/category/${movie.category}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-arrow-left-line" /> 返回{getCategoryName(movie.category)}列表
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-primary-600">
                  <span className="h-1 w-1 rounded-full bg-accent-500" />
                  You may also like
                </span>
                <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  相关影片推荐
                </h2>
                <p className="mt-1.5 text-sm text-foreground-500">
                  根据分类与标签匹配，看看同类型的其它选择。
                </p>
              </div>
              <Link
                to={`/category/${movie.category}`}
                className="flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                更多{getCategoryName(movie.category)} <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t border-background-200 bg-background-50/95 px-4 py-2.5 backdrop-blur lg:hidden">
        <button
          type="button"
          onClick={handleShare}
          className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2.5 text-sm text-foreground-700"
        >
          <i className="ri-share-forward-line" /> 分享
        </button>
        <button
          type="button"
          onClick={() => setWatchOpen(true)}
          className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-sm font-medium text-background-50"
        >
          <i className="ri-play-circle-line text-base" /> 立即观看
        </button>
      </div>

      {toast ? (
        <div className="fixed left-1/2 top-24 z-[60] -translate-x-1/2 animate-fade-in rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50">
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400" />
            {toast}
          </span>
        </div>
      ) : null}

      {watchOpen ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 px-4">
          <div className="w-full max-w-sm animate-fade-up rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-play-circle-line text-2xl" />
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
              演示站点提示
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              当前页面仅展示《{movie.title}》的影片资料，本站不提供真实播放与下载服务。
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setWatchOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              >
                知道了
              </button>
              <button
                type="button"
                onClick={() => {
                  setWatchOpen(false);
                  handleShare();
                }}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                复制链接
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}